import { useEffect, useState } from 'react';
import Text from '@sber-site/sbercraft-nova-kit/components/Text';
import bem from 'bem-cn';
import Icon from '@sber-site/sbercraft-nova-kit/components/Icon';
import Grid from '@sber-site/sbercraft-nova-kit/composite/Grid';

import { loadSettings } from './api-typed';
import type { RetentionProps } from './types';
import { useStyles } from '../../LoaderContext';
import RetentionButton from './components/RetentionButton/RetentionButton';
import { getDataFromJSON } from './utils/getDataFromJSON';
import { ReactComponent as CloseIcon } from './images/CloseIcon.svg';
import creditCardIcon from './images/creditCardIcon.svg';
import debitCardIcon from './images/debitCardIcon.svg';
import validateData from './utils/validateData';
import { pushRetentionEmpty, pushRetentionShow, sendToAnalytics } from './analytics/web-analytics';
import formatFirstName from './utils/formatFirstName';
import getLocalStorageData from './utils/getLocalStorageData';
import determinePriority from './utils/determinePriority';
import Survery from './components/Survery/Survery';
import determineSurvery from './utils/determineSurvery';
import addBlankTargets from './utils/addBlankTargets';

import * as css from './Retention.css';

const cn = bem('retention');

const RetentionWidget = (props: RetentionProps) => {
    useStyles(css);
    const [settings, setSettings] = useState({});
    const [isClosedByUser, setIsClosedByUser] = useState(false);
    const [isShownWidget, setIsShownWidget] = useState(false);
    const [isSurveryShow, setIsSurveryShow] = useState(false);
    const [surveryData, setSurveryData] = useState(null);

    const debitCardDeliveryRetentionData = getDataFromJSON(settings.RETENTION_DCARD_DELIVERY);
    const creditCardRetentionData = getDataFromJSON(settings.RETENTION_CCARD);
    const debitCardRetentionData = getDataFromJSON(settings.RETENTION_DCARD);

    const debitCardDeliverySurveryData = getDataFromJSON(settings.RETENTION_DCARD_DELIVERY_SURVEY);
    const creditCardSurveryData = getDataFromJSON(settings.RETENTION_CCARD_SURVEY);
    const debitCardSurveryData = getDataFromJSON(settings.RETENTION_DCARD_SURVEY);

    const creditCardLocalStorageData = getLocalStorageData('ccard_success')?.[0];
    const debitCardDeliveryLocalStorageData = getLocalStorageData('dcard_delivery_success')?.[0];
    const debitCardLocalStorageData = getLocalStorageData('dcard_success')?.[0];
    const noStretchingArray = getLocalStorageData('no_stretching');

    const getSettingsData = async () => {
        const settingsData = await loadSettings(props.serviceName);
        setSettings(settingsData);
    };

    useEffect(() => {
        getSettingsData();
    }, []);

    const { currentCard, variableForText, text } = determinePriority(
        creditCardLocalStorageData,
        debitCardDeliveryLocalStorageData,
        debitCardLocalStorageData,
        noStretchingArray,
        creditCardRetentionData,
        debitCardDeliveryRetentionData,
        debitCardRetentionData
    );

    const currentLocalStorageData = {
        creditCard: creditCardLocalStorageData,
        debitCardDelivery: debitCardDeliveryLocalStorageData,
        debitCard: debitCardLocalStorageData,
    };

    const currentRetentionData = {
        creditCard: creditCardRetentionData,
        debitCardDelivery: debitCardDeliveryRetentionData,
        debitCard: debitCardRetentionData,
    };

    const valuesnoStretchingArray = {
        creditCard: 'ccard',
        debitCardDelivery: 'dcard_delivery',
        debitCard: 'dcard',
    };

    const dataFromCurrentLocalStorage = currentLocalStorageData[currentCard];
    const textFromServiceVar = currentRetentionData[currentCard];
    const stretchingValue = valuesnoStretchingArray[currentCard];

    const deliveryType = currentCard === 'debitCard' ? 'office' : dataFromCurrentLocalStorage?.orderType;
    const eventLabelWithType = `${valuesnoStretchingArray[currentCard]}_${deliveryType}_${variableForText}`;

    const textButton =
        deliveryType === 'office' ? textFromServiceVar?.textButton : textFromServiceVar?.textCourierButton;
    const linkButton =
        deliveryType === 'office' ? textFromServiceVar?.linkButton : textFromServiceVar?.linkCourierButton;

    useEffect(() => {
        if (textFromServiceVar && variableForText) {
            pushRetentionEmpty(textFromServiceVar, variableForText, eventLabelWithType);
        }
    }, [textFromServiceVar]);

    useEffect(() => {
        if (textFromServiceVar) {
            const newData = determineSurvery(
                currentCard,
                deliveryType,
                debitCardDeliverySurveryData,
                creditCardSurveryData,
                debitCardSurveryData
            );
            if (JSON.stringify(newData) !== JSON.stringify(surveryData) && !isClosedByUser) {
                setSurveryData(newData);
            }
        }
    }, [textFromServiceVar, surveryData]);

    useEffect(() => {
        pushRetentionShow(isShownWidget, eventLabelWithType);
    }, [isShownWidget]);

    const validText = text
        ?.replace(/%DELIVERY%/g, `${dataFromCurrentLocalStorage?.deliveryDate?.split('-')[2]}`)
        .replace(/%BEGINTIME%/g, `${dataFromCurrentLocalStorage?.beginTime}`)
        .replace(/%ENDTIME%/g, `${dataFromCurrentLocalStorage?.endTime}`)
        .replace(/%MONTH%/g, `${dataFromCurrentLocalStorage?.month?.toLowerCase()}`)
        .replace(/%FIRSTNAME%/g, formatFirstName(`${dataFromCurrentLocalStorage?.firstName}`));

    // добавление в локалсторадж по клику на крестик в массив no_stretching тип карты, чтобы потом не отражалось у клиента данная инфа
    const addValueToArrayNoStretching = (typeCard: string) => {
        const updatedArray = !noStretchingArray ? [typeCard] : [...noStretchingArray, typeCard];
        localStorage.setItem('no_stretching', JSON.stringify(updatedArray));
        setIsClosedByUser(true);
    };

    const conditionForShowWidget =
        currentCard && validateData(dataFromCurrentLocalStorage) && !isClosedByUser && validText;
    const showButton = !!textButton && !!linkButton;

    function checkClickLink(e) {
        if (e.target.tagName.toLowerCase() === 'a') {
            sendToAnalytics('retention_click_link', e.target?.innerText);
        }
    }

    const handleServeryShow = (booleanValue: boolean) => {
        setIsSurveryShow(booleanValue);
    };

    return (
        <>
            {conditionForShowWidget && (
                <div className={cn()} onLoad={() => setIsShownWidget(true)}>
                    <Grid fixed={true} className={cn('grid')()}>
                        <div className={cn('container')()}>
                            <div className={cn('left')()} onClick={(e) => checkClickLink(e)}>
                                <div className={cn('icon')()}>
                                    <img
                                        src={currentCard === 'creditCard' ? creditCardIcon : debitCardIcon}
                                        alt=''
                                        role='presentation'
                                    />
                                </div>
                                <Text
                                    className={showButton ? cn('text')() : cn('text', { withoutMarginBottom: true })()}
                                    size='sm'
                                    html={addBlankTargets(validText)}
                                />
                            </div>
                            <div className={cn('right')()}>
                                {showButton && (
                                    <RetentionButton
                                        textButton={textButton}
                                        textFromServiceVar={textFromServiceVar}
                                        linkButton={linkButton}
                                        hasSurveryVariables={!!surveryData}
                                        handleServeryShow={handleServeryShow}
                                    />
                                )}
                                <div className={cn('icon-close')()}>
                                    <Icon
                                        onClick={() => {
                                            addValueToArrayNoStretching(stretchingValue);
                                            sendToAnalytics('retention_click_close');
                                        }}
                                    >
                                        <CloseIcon />
                                    </Icon>
                                </div>
                            </div>
                        </div>
                    </Grid>
                </div>
            )}
            {isSurveryShow && (
                <Survery
                    isSurveryShow={isSurveryShow}
                    handleServeryShow={handleServeryShow}
                    closeRetention={() => addValueToArrayNoStretching(stretchingValue)}
                    surveryData={surveryData}
                    eventLabelWithType={eventLabelWithType}
                />
            )}
        </>
    );
};

export default RetentionWidget;
